import React, { useEffect, useState } from 'react';

import { isDebugAnalyticsActive } from '@editor/utils/dev';
import logger from '@editor/utils/logger';
import {
  BrowserCookieStickyBucketService,
  FeatureDefinition,
  GrowthBook,
  GrowthBookProvider as GrowthBookProviderComponent,
  useFeatureIsOn,
} from '@growthbook/growthbook-react';
import analytics from '@repo/analytics';
import env from '@repo/env';
import { BREAKPOINTS } from '@repo/ui/queries';
import Cookies from 'js-cookie';

import { AB_TRACKINGCALLBACK } from '../global/analytics/events';
import { REACT_APP_GROWTHBOOK_SDK_API_KEY } from '../global/constants';
import { useUserStore } from '../stores/userStore';
import { getDeviceId } from '../stores/userStore/utils';

import monitoring from './monitoring';

export let growthbook: GrowthBook;

/**
 * Check if playwright tests are running
 * @returns boolean
 */
const isPlaywright = (): boolean => {
  return typeof window !== 'undefined' && Boolean(window.isPlaywright);
};

/**
 * IMPORTANT: If you are relying on an experiment (e.g. A/B), use the `useFeatureIsOn` hook.
 * Using growthbook.isOn directly will not work as expected.
 */

export const useTeamAccountsFeature = (): boolean => {
  return useFeatureIsOn('team-accounts');
};

export const useHideAccountUpgradeModal = (): boolean | null => {
  return useFeatureIsOn('website-hide-account-upgrade-modal');
};

export const roundCornersKey = 'round-corners';
export const useRoundCornersFeature = (): boolean => {
  return useFeatureIsOn(roundCornersKey);
};
export const isRoundedCornersFeatureEnabled = (): boolean =>
  Boolean(growthbook?.isOn(roundCornersKey));

export const useShapeBuilderFeature = (): boolean => {
  return useFeatureIsOn('shape-builder-v2');
};

// Clean up reference: https://app.clickup.com/t/86c37rx2v
export const useVectorizeTextFeature = (): boolean => {
  return useFeatureIsOn('vectorize-text');
};

// Clean up reference: https://app.clickup.com/t/86c37rx2v
export const isVectorizeTextFeatureEnabled = (): boolean => {
  return Boolean(growthbook?.isOn('vectorize-text'));
};

// Cleanup task: https://app.clickup.com/t/86c388h3h
export const isShapeBuilderSingleIllustrationFeatureEnabled = (): boolean => {
  return Boolean(growthbook?.isOn('shape-builder-single-illustration'));
};

export const penKey = 'pen';
export const usePenFeature = (): boolean => {
  const isVectorEditingFeatureEnabled = useFeatureIsOn(vectorEditingKey);
  const isPenFeatureEnabled = useFeatureIsOn(penKey);
  return isVectorEditingFeatureEnabled && isPenFeatureEnabled;
};
export const isPenFeatureEnabled = (): boolean =>
  Boolean(growthbook?.isOn(penKey)) &&
  Boolean(growthbook?.isOn(vectorEditingKey));

const vectorEditingKey = 'vector-editing';
export const isVectorEditingFeatureEnabled = (): boolean =>
  Boolean(growthbook?.isOn(vectorEditingKey));

// Sticky bucketing caches previous usages of features (https://docs.growthbook.io/app/sticky-bucketing)
const stickyBucketService =
  typeof window !== 'undefined'
    ? new BrowserCookieStickyBucketService({
        jsCookie: Cookies,
        cookieAttributes: {
          path: '/',
          domain: env.cookieDomain,
          sameSite: 'lax',
          secure: env.nodeEnv === 'production',
          expires: 30,
        },
      })
    : undefined;

/**
 * Initialize Growthbook
 * @returns boolean
 */
const initGrowthbook = (): void => {
  if (!REACT_APP_GROWTHBOOK_SDK_API_KEY) {
    // @TODO: Should it load in cypress/cromatic?
    console.warn('Growthbook: No SDK API key provided.');
    return;
  }

  // Prevent init issues on test environments
  const url =
    typeof window === 'undefined' || !window.location
      ? 'test-url'
      : window.location.href;

  growthbook = new GrowthBook({
    apiHost: 'https://cdn.growthbook.io',
    clientKey: REACT_APP_GROWTHBOOK_SDK_API_KEY,
    enableDevMode: env.nodeEnv === 'development' || isDebugAnalyticsActive(),
    subscribeToChanges: false,
    debug: false,

    // Disable cache when running in Playwright to allow intercepting the requests reliably
    disableCache: isPlaywright() ? true : undefined,

    // Initialize always with base attributes
    // This ensures logged out users are tracked via their device id
    attributes: {
      ...getGrowthbookBaseAttributes(),
    },

    url,
    stickyBucketService,
    trackingCallback: (experiment, result): void => {
      // Skip tracking in test environment
      if (process.env.NODE_ENV === 'test') {
        return;
      }

      const attributes = growthbook.getAttributes();
      analytics.track(
        AB_TRACKINGCALLBACK,
        {
          experimentId: experiment.key,
          variationId: result.key,
          featureId: result.featureId,
          deviceId: attributes?.deviceId,
          // timestamp in microseconds (16 digits) for Pub/Sub
          timestamp: new Date().getTime() * 1000,
          // userId is added in the API as enrichment
        },
        {
          plugins: {
            // @TODO: remove after comparing and approving the results
            mixpanel: true,
            // stream to pub/sub
            // apiEventStreaming: true,
          },
        }
      );
    },
  });
};

/**
 * Gets the base attributes (logged out) for Growthbook
 * It is used to set the attributes when we reset the user
 * @returns Record<string, unknown>
 */
const getGrowthbookBaseAttributes = (): Record<string, unknown> => {
  let isMobile = false;

  if (typeof window !== 'undefined') {
    // We need to use window here because we need this to be based on window.innerWidth
    // and isMobileExcludingIpad uses userAgent
    isMobile = window.innerWidth < BREAKPOINTS.largeTabletMin;
  }

  return {
    isMobile,
    deviceId:
      process.env.NODE_ENV === 'test' ? 'test-device-id' : getDeviceId(),
  };
};

/**
 * Get the attributes for Growthbook
 * It includes the base attributes and user attributes
 * @returns Record<string, unknown>
 */
const getGrowthbookAttributes = (): Record<string, unknown> => {
  const user = useUserStore?.getState().user;

  const attributes = {
    ...(growthbook?.getAttributes() ?? {}),
    ...(user && {
      id: user.id,
      email: user.email,
      userId: user.id,
      plan: user.plan || 'FREE',
      featureGroups: user.featureGroups,
      country: user.country?.current,
      // createdAt is not always set in tests, causing them to fail
      createdAt: user.createdAt ? new Date(user.createdAt).toISOString() : '',
    }),
  };

  return attributes;
};

/**
 * Reset the attributes for Growthbook
 */
export const resetGrowthbookAttributes = async (): Promise<void> => {
  if (!growthbook) {
    console.warn(
      'Growthbook: instance is not initialized, skipping attribute reset'
    );
    return;
  }

  growthbook.setAttributes(getGrowthbookBaseAttributes());
};

/**
 * Set the attributes for Growthbook when the user is set
 */
export const setGrowthbookAttributes = async (): Promise<void> => {
  // Early return if growthbook is not initialized
  if (!growthbook) {
    console.warn(
      'Growthbook: instance is not initialized, skipping attribute setting'
    );
    return;
  }

  try {
    growthbook.setAttributes(getGrowthbookAttributes());
  } catch (error) {
    console.error('Error setting Growthbook attributes:', error);
  }
};

/**
 * Update the URL for Growthbook when the URL changes (e.g. on navigation)
 * This is needed to track the URL changes for the visual editor
 */
export const updateGrowthBookURL = (): void => {
  // Early return if window or growthbook is not available
  if (typeof window === 'undefined' || !window.location) {
    console.warn(
      'Growthbook: window.location is not available, skipping URL update'
    );
    return;
  }

  if (!growthbook) {
    console.warn(
      'Growthbook: instance is not initialized, skipping URL update'
    );
    return;
  }

  growthbook.setURL(window.location.href);
};

/**
 * Initialize Growthbook
 */
try {
  initGrowthbook();
} catch (error) {
  console.error('Error initializing Growthbook:', error);
}

export const GrowthbookProvider: React.FC<
  React.PropsWithChildren<{ showWhileLoading?: boolean }>
> = ({ children, showWhileLoading = false }) => {
  // we do not want to block pages from rendering on the server side in website because growthbook is not loaded
  const [loaded, setLoaded] = useState(showWhileLoading);

  useEffect(() => {
    const loadGrowthbook = async (): Promise<void> => {
      try {
        await growthbook.init();
        setLoaded(true);

        // Base is used by data to debug experimentation, please keep on this evaluation after init.
        const testFeature = growthbook.isOn('base-feature');
        logger.debug('[Growthbook] base-feature', testFeature);
      } catch (error) {
        monitoring.captureException(error, 'Growthbook failed to load');
        logger.error('[Growthbook] failed to load', error);
      }
    };

    loadGrowthbook();
  }, []);

  if (!REACT_APP_GROWTHBOOK_SDK_API_KEY) {
    return <>{children}</>;
  }

  return (
    <GrowthBookProviderComponent growthbook={growthbook}>
      {loaded ? children : null}
    </GrowthBookProviderComponent>
  );
};

export const getAllFeatureValues = (): Record<string, FeatureDefinition> => {
  const allFeatures = growthbook.getFeatures();

  // e.g. { 'feature1': true, 'feature2': false }
  return Object.entries(allFeatures).reduce<Record<string, FeatureDefinition>>(
    (acc, [feature, { defaultValue }]) => {
      acc[feature] = growthbook.getFeatureValue(feature, defaultValue);
      return acc;
    },
    {}
  );
};
